import { useState, useEffect } from 'react';

interface DeviceInfo {
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
    isTouchDevice: boolean;
    isHighDPI: boolean;
    supportsHover: boolean;
    screenWidth: number;
    screenHeight: number;
}

export const useDeviceDetection = (): DeviceInfo => {
    const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        isTouchDevice: false,
        isHighDPI: false,
        supportsHover: true,
        screenWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
        screenHeight: typeof window !== 'undefined' ? window.innerHeight : 768,
    });

    useEffect(() => {
        const updateDeviceInfo = () => {
            const width = window.innerWidth;
            const height = window.innerHeight;
            const isMobile = width < 1400; // Extended to include tablets in mobile layout
            const isTablet = width >= 768 && width < 1400; // Tablets use mobile layout
            const isDesktop = width >= 1400; // Desktop starts at 1400px
            
            // Detect touch capability
            const isTouchDevice = (
                'ontouchstart' in window ||
                navigator.maxTouchPoints > 0 ||
                // @ts-ignore - Legacy IE support
                navigator.msMaxTouchPoints > 0
            );
            
            // Detect high DPI displays
            const isHighDPI = (
                window.devicePixelRatio > 1.5 ||
                (window.matchMedia && window.matchMedia(
                    '(-webkit-min-device-pixel-ratio: 1.5), (min-resolution: 144dpi), (min-resolution: 1.5dppx)'
                ).matches)
            );
            
            // Detect hover support
            const supportsHover = window.matchMedia('(hover: hover)').matches;

            setDeviceInfo({
                isMobile,
                isTablet,
                isDesktop,
                isTouchDevice,
                isHighDPI,
                supportsHover,
                screenWidth: width,
                screenHeight: height,
            });
        };

        // Initial detection
        updateDeviceInfo();

        // Listen for resize events
        const debouncedUpdate = debounce(updateDeviceInfo, 150);
        window.addEventListener('resize', debouncedUpdate);
        window.addEventListener('orientationchange', debouncedUpdate);

        return () => {
            window.removeEventListener('resize', debouncedUpdate);
            window.removeEventListener('orientationchange', debouncedUpdate);
        };
    }, []);

    return deviceInfo;
};

// Simple debounce function to avoid excessive updates
function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
): (...args: Parameters<T>) => void {
    let timeout: number;
    return (...args: Parameters<T>) => {
        clearTimeout(timeout);
        timeout = window.setTimeout(() => func(...args), wait);
    };
}